import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { TrendingUp, Loader2, ArrowUp, ArrowDown, Minus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INDICATORS = [
  { code: 'NY.GDP.MKTP.KD.ZG', name: 'GDP Growth', unit: '%', color: '#004ac6' },
  { code: 'FP.CPI.TOTL.ZG', name: 'Inflation', unit: '%', color: '#ef4444' },
  { code: 'SL.UEM.TOTL.ZS', name: 'Unemployment', unit: '%', color: '#f59e0b' },
  { code: 'NE.TRD.GNFS.ZS', name: 'Trade/GDP', unit: '%', color: '#059669' },
  { code: 'GC.DOD.TOTL.GD.ZS', name: 'Govt Debt/GDP', unit: '%', color: '#8b5cf6' },
  { code: 'DT.DOD.DECT.GN.ZS', name: 'Ext. Debt/GNI', unit: '%', color: '#dc2626' },
];

function CountryTrendView({ countryId, countryName }) {
  const [trends, setTrends] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!countryName) return;
    setLoading(true);
    
    // Fetch all 5 indicators in parallel
    Promise.all(
      INDICATORS.map(ind =>
        axios.get(`${API}/wb-trends/${ind.code}`, { timeout: 50000 })
          .then(res => ({ code: ind.code, data: res.data }))
          .catch(() => ({ code: ind.code, data: null }))
      )
    ).then(results => {
      const map = {};
      results.forEach(r => {
        if (r.data?.trends?.[countryName]) {
          map[r.code] = { years: r.data.years, values: r.data.trends[countryName] };
        }
      });
      setTrends(map);
    }).finally(() => setLoading(false));
  }, [countryName]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-5 h-5 animate-spin text-[#004ac6] mr-2" />
        <span className="text-xs text-[#434655]">Loading World Bank economic data for {countryName}...</span>
      </div>
    );
  }

  const hasData = Object.keys(trends).length > 0;
  if (!hasData) {
    return (
      <div className="text-center py-8">
        <p className="text-xs text-[#434655] italic">No World Bank trend data available for {countryName}.</p>
      </div>
    );
  }

  return (
    <div data-testid="country-trend-view" className="space-y-6">
      {/* Key Metrics Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {INDICATORS.map(ind => {
          const trendData = trends[ind.code];
          if (!trendData) return (
            <div key={ind.code} className="bg-[#f8f9ff] p-3 rounded-lg">
              <span className="text-[9px] text-[#434655] uppercase tracking-wider font-bold">{ind.name}</span>
              <p className="text-lg font-bold text-[#c3c6d7] mt-1">—</p>
            </div>
          );
          
          const years = Object.keys(trendData.values).sort();
          const latestYear = years[years.length - 1];
          const prevYear = years[years.length - 2];
          const latest = trendData.values[latestYear];
          const prev = prevYear ? trendData.values[prevYear] : null;
          const change = prev != null ? latest - prev : null;
          
          return (
            <div key={ind.code} className="bg-white border border-[#c3c6d7]/20 p-3 rounded-lg">
              <span className="text-[9px] text-[#434655] uppercase tracking-wider font-bold">{ind.name}</span>
              <div className="flex items-end gap-1.5 mt-1">
                <span className="text-lg font-bold text-[#0b1c30]">{latest?.toFixed(1)}<span className="text-xs font-normal text-[#434655]">{ind.unit}</span></span>
              </div>
              <div className="flex items-center gap-1 mt-0.5">
                {change != null && (
                  <>
                    {change > 0.1 ? <ArrowUp className="w-3 h-3 text-green-600" /> :
                     change < -0.1 ? <ArrowDown className="w-3 h-3 text-red-500" /> :
                     <Minus className="w-3 h-3 text-[#434655]" />}
                    <span className={`text-[10px] font-bold ${change > 0.1 ? 'text-green-600' : change < -0.1 ? 'text-red-500' : 'text-[#434655]'}`}>
                      {change > 0 ? '+' : ''}{change?.toFixed(1)}pp
                    </span>
                  </>
                )}
                <span className="text-[9px] text-[#434655]">({latestYear})</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Line Charts Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {INDICATORS.map(ind => {
          const trendData = trends[ind.code];
          if (!trendData) return null;
          
          const chartData = Object.entries(trendData.values)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([year, value]) => ({ year, value: parseFloat(value?.toFixed(2)) }));
          
          if (chartData.length < 2) return null;
          
          return (
            <div key={ind.code} className="bg-white border border-[#c3c6d7]/20 p-4 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-xs font-bold text-[#0b1c30]">{ind.name}</h4>
                <span className="text-[9px] text-[#434655]">{chartData[0].year}–{chartData[chartData.length - 1].year}</span>
              </div>
              <ResponsiveContainer width="100%" height={160}>
                {ind.code === 'GC.DOD.TOTL.GD.ZS' || ind.code === 'NE.TRD.GNFS.ZS' ? (
                  <BarChart data={chartData} margin={{ top: 5, right: 5, left: -15, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="year" tick={{ fontSize: 9, fill: '#434655' }} />
                    <YAxis tick={{ fontSize: 9, fill: '#434655' }} width={40} />
                    <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #E5E7EB' }}
                      formatter={v => [`${v}${ind.unit}`, ind.name]} />
                    <Bar dataKey="value" radius={[3, 3, 0, 0]}>
                      {chartData.map((_, i) => (
                        <Cell key={i} fill={i === chartData.length - 1 ? ind.color : `${ind.color}66`} />
                      ))}
                    </Bar>
                  </BarChart>
                ) : (
                  <LineChart data={chartData} margin={{ top: 5, right: 5, left: -15, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="year" tick={{ fontSize: 9, fill: '#434655' }} />
                    <YAxis tick={{ fontSize: 9, fill: '#434655' }} width={40} />
                    <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #E5E7EB' }}
                      formatter={v => [`${v}${ind.unit}`, ind.name]} />
                    <Line type="monotone" dataKey="value" stroke={ind.color} strokeWidth={2.5}
                      dot={{ r: 3.5, fill: ind.color, stroke: '#fff', strokeWidth: 2 }}
                      activeDot={{ r: 5.5 }} />
                  </LineChart>
                )}
              </ResponsiveContainer>
            </div>
          );
        })}
      </div>
      <p className="text-[9px] text-[#9CA3AF]">Source: World Bank Open Data — api.worldbank.org</p>
    </div>
  );
}

export default CountryTrendView;
